import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Briefcase, FileText, Upload, Trash2, Loader2, Lock, Globe, BadgeCheck, ChevronDown, ChevronUp } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import { getMediaUrl } from '../../utils/mediaUrl';
import { portfolioTexts, PORTFOLIO_FIELDS } from './portfolioTexts';

export const LetterViewer = ({ url, type, L }) => {
  const [open, setOpen] = useState(false);
  if (!url) return null;
  return (
    <div className="rounded-xl bg-white/5 border border-white/10 p-3" data-testid="portfolio-letter">
      <div className="flex items-center gap-2 text-sm"><FileText className="w-4 h-4 text-fuchsia-400" /><span className="flex-1 font-semibold">{L.letter}</span>
        <button onClick={() => setOpen(o => !o)} className="px-3 py-1 rounded-full bg-fuchsia-500/30 text-xs flex items-center gap-1" data-testid="portfolio-letter-toggle">{open ? <><ChevronUp className="w-3 h-3" />{L.letterClose}</> : <><ChevronDown className="w-3 h-3" />{L.letterOpen}</>}</button>
      </div>
      {open && (type === 'pdf'
        ? <iframe title="letter" src={getMediaUrl(url)} className="w-full h-[60vh] mt-3 rounded-lg bg-white" />
        : <img src={getMediaUrl(url)} alt="letter" className="w-full mt-3 rounded-lg" />)}
    </div>
  );
};

export const PortfolioView = ({ data, L }) => {
  const filled = PORTFOLIO_FIELDS.filter(f => (data[f] || '').trim());
  if (!filled.length && !data.letter_url) return <p className="text-sm text-gray-400" data-testid="portfolio-empty">{L.empty}</p>;
  return (
    <div className="space-y-3" data-testid="portfolio-view">
      {filled.map(f => (
        <div key={f} className="rounded-xl bg-white/5 border border-white/10 p-3">
          <div className="text-[11px] uppercase tracking-wide text-fuchsia-300 mb-1">{L[f]}</div>
          <p className={`text-sm whitespace-pre-wrap ${f === 'headline' ? 'font-bold text-base' : ''}`}>{data[f]}</p>
        </div>
      ))}
      <LetterViewer url={data.letter_url} type={data.letter_type} L={L} />
    </div>
  );
};

export const PortfolioEditor = () => {
  const { language } = useLanguage();
  const L = portfolioTexts(language);
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  useEffect(() => { axios.get('/api/portfolio/me').then(r => setData(r.data)).catch(() => setData({})); }, []);
  if (!data) return <Loader2 className="w-5 h-5 animate-spin text-fuchsia-400" />;

  const save = async (patch) => {
    setSaving(true);
    try { const r = await axios.put('/api/portfolio/me', patch || Object.fromEntries(PORTFOLIO_FIELDS.map(f => [f, data[f] || '']))); setData(r.data); toast.success(L.saved); }
    catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); } finally { setSaving(false); }
  };
  const upload = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    const fd = new FormData(); fd.append('file', f); setUploading(true);
    try { const r = await axios.post('/api/portfolio/me/letter', fd); setData(d => ({ ...d, ...r.data })); }
    catch (err) { toast.error(err.response?.data?.detail || 'Upload'); } finally { setUploading(false); e.target.value = ''; }
  };
  const removeLetter = async () => { await axios.delete('/api/portfolio/me/letter'); setData(d => ({ ...d, letter_url: null, letter_type: null })); };
  const VIS = [['public', Globe, L.public, L.publicSub], ['pro_only', BadgeCheck, L.pro_only, L.proSub], ['private', Lock, L.private, L.privateSub]];
  const inputCls = 'w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-fuchsia-500/60';

  return (
    <div className="space-y-4" data-testid="portfolio-editor">
      <div className="flex items-start gap-3"><Briefcase className="w-5 h-5 text-fuchsia-400 mt-0.5" /><div><h3 className="font-bold">{L.title}</h3><p className="text-xs text-gray-400">{L.intro}</p></div></div>
      <div>
        <div className="text-[11px] text-gray-400 mb-1.5">{L.visibility}</div>
        <div className="grid grid-cols-3 gap-2">
          {VIS.map(([v, Icon, lab, sub]) => (
            <button key={v} onClick={() => save({ visibility: v })} className={`p-2 rounded-xl border text-left ${data.visibility === v ? 'bg-fuchsia-500/20 border-fuchsia-400' : 'bg-white/5 border-white/10'}`} data-testid={`portfolio-vis-${v}`}>
              <Icon className="w-4 h-4 text-fuchsia-300 mb-1" /><div className="text-xs font-semibold">{lab}</div><div className="text-[10px] text-gray-400 leading-tight">{sub}</div>
            </button>
          ))}
        </div>
      </div>
      {PORTFOLIO_FIELDS.map(f => (
        f === 'headline'
          ? <input key={f} className={inputCls} placeholder={L[f]} value={data[f] || ''} onChange={e => setData({ ...data, [f]: e.target.value })} data-testid={`portfolio-${f}`} />
          : <textarea key={f} rows={2} className={inputCls} placeholder={L[f]} value={data[f] || ''} onChange={e => setData({ ...data, [f]: e.target.value })} data-testid={`portfolio-${f}`} />
      ))}
      <button onClick={() => save()} disabled={saving} className="w-full py-2.5 rounded-full bg-gradient-to-r from-fuchsia-500 to-pink-500 text-white text-sm font-semibold disabled:opacity-50" data-testid="portfolio-save">{saving ? <Loader2 className="w-4 h-4 animate-spin inline" /> : L.save}</button>
      <div className="flex gap-2">
        <button onClick={() => fileRef.current?.click()} disabled={uploading} className="flex-1 py-2 rounded-xl border border-dashed border-white/30 text-xs flex items-center justify-center gap-1" data-testid="portfolio-letter-upload">{uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}{L.letterAdd}</button>
        {data.letter_url && <button onClick={removeLetter} className="px-3 rounded-xl bg-red-500/20 text-red-300 text-xs flex items-center gap-1" data-testid="portfolio-letter-remove"><Trash2 className="w-3.5 h-3.5" />{L.letterRemove}</button>}
        <input ref={fileRef} type="file" accept="application/pdf,image/*" className="hidden" onChange={upload} />
      </div>
      <LetterViewer url={data.letter_url} type={data.letter_type} L={L} />
    </div>
  );
};

export const PublicPortfolio = ({ userId }) => {
  const { language } = useLanguage();
  const L = portfolioTexts(language);
  const [data, setData] = useState(null);
  useEffect(() => { axios.get(`/api/portfolio/user/${userId}`).then(r => setData(r.data)).catch(() => setData({ visible: false })); }, [userId]);
  if (!data) return <Loader2 className="w-5 h-5 animate-spin text-fuchsia-400" />;
  if (!data.visible) return <p className="text-sm text-gray-400 flex items-center gap-2" data-testid="portfolio-hidden"><Lock className="w-4 h-4" />{data.visibility === 'pro_only' ? L.proOnlyHidden : L.hidden}</p>;
  return <PortfolioView data={data} L={L} />;
};
